/**
 * screens/admin/adminDeliveryFailures.tsx — Delivery failures
 * (SKPLATFORM-UICC-001 CH-U5).
 *
 * The outbox dead-letter queue: events that exhausted their delivery retries
 * and are now stuck (SKPLATFORM-CC-002 CH-6.2).
 *
 * ── What this screen is FOR ───────────────────────────────────────────────
 * Investigating a push, not watching for one. Alerts arrive on the ops channel
 * the moment an event enters the DLQ — that is CH-6.2's whole point, so that
 * noticing never depends on someone having this tab open. The empty state says
 * so out loud, because a screen that looks like a monitor invites people to
 * treat it as one.
 *
 * ── Two things deliberately absent ────────────────────────────────────────
 *   · **No delete.** A failed row is the only record that an event should have
 *     been delivered and was not. Rows leave this list by being requeued
 *     successfully or by explicit engineering intervention — never because a
 *     console button destroyed the evidence. The API has no delete route either.
 *   · **No auto-refresh.** Polling would make the list churn under someone
 *     reading an error message. Refresh is a button.
 *
 * Sorted oldest-first: the event stuck longest is the most important row, and a
 * newest-first list buries it under whatever broke most recently.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, StatusBadge, SideDrawer } from '../../components/platform/primitives';
import { PButton, PEmpty } from '../../components/platform/controls';
import { FS, FW } from '../../utils/platformTokens';
import { listOutboxDeadLetters, requeueOutboxEvent } from '../../services/platform/platformApi';
import type { OutboxDeadLetter } from '../../schema/platform/types';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { PermissionDenied } from '../shell/chrome';

/** Errors are long. Truncate in the row, expand on tap — never drop. */
const ERROR_PREVIEW = 90;

const AdminDeliveryFailures: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();
  const { isPlatformAdmin } = usePlatform();

  const [rows, setRows] = useState<OutboxDeadLetter[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [payloadFor, setPayloadFor] = useState<OutboxDeadLetter | null>(null);
  const [requeuing, setRequeuing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setRows(await listOutboxDeadLetters());
    } catch {
      // Distinguished from "no failures": an empty list because the call failed
      // must never render as the reassuring "No delivery failures." empty state.
      setFailed(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Defence in depth. AppShell already refuses the whole admin AREA to anyone
  // without this flag, so reaching here means the shell gate changed — but a
  // screen that requeues stuck events should not depend on a caller elsewhere
  // still checking. `usePlatform().isPlatformAdmin` is the console's own source
  // of truth; `useSession().role` speaks a different vocabulary
  // (ADMIN/EDITOR/VIEWER) and comparing it to 'platform_admin' would deny
  // everyone.
  if (!isPlatformAdmin) return <PermissionDenied />;

  const requeue = async (row: OutboxDeadLetter) => {
    setRequeuing(row.id);
    try {
      await requeueOutboxEvent(row.id);
      toast.success('Event requeued. It will leave this list once it delivers.');
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not requeue that event.'));
    } finally {
      setRequeuing(null);
    }
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Delivery failures"
        sub="Events that exhausted their delivery retries. Oldest first."
        action={<PButton kind="ghost" icon="refresh" onPress={load} disabled={loading}>Refresh</PButton>}
      />

      <Panel bodyStyle={{ padding: 0 }}>
        {loading ? (
          <View style={{ padding: 24 }}>
            <Text style={{ color: c.ink3 }}>Loading…</Text>
          </View>
        ) : failed ? (
          <View style={{ padding: 24 }}>
            <PEmpty icon="warn" title="Could not load delivery failures">
              The list is unavailable right now. This does not mean there are none —
              try refreshing.
            </PEmpty>
          </View>
        ) : rows.length === 0 ? (
          <View style={{ padding: 24 }}>
            <PEmpty icon="check" title="No delivery failures">
              Alerts will arrive via the ops channel if one occurs.
            </PEmpty>
          </View>
        ) : (
          <DataTable<OutboxDeadLetter>
            minWidth={980}
            keyExtractor={(r) => r.id}
            rows={rows}
            columns={[
              {
                key: 'event', header: 'Event', width: 240,
                render: (r) => (
                  <View>
                    <Text style={{ fontWeight: FW.medium, color: c.ink, fontSize: FS.md }}>{r.event_type}</Text>
                    <Text style={{ fontFamily: 'monospace', fontSize: FS.sm, color: c.ink3 }}>{r.id}</Text>
                  </View>
                ),
              },
              {
                key: 'target', header: 'Target', width: 150,
                render: (r) => (
                  <View style={{ gap: 3 }}>
                    {r.deliveries.length === 0 ? (
                      <Text style={{ color: c.ink3, fontSize: FS.md }}>—</Text>
                    ) : (
                      r.deliveries.map((d) => (
                        <View key={d.destination} style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                          <Text style={{ fontFamily: 'monospace', fontSize: FS.sm, color: c.ink2 }}>{d.destination}</Text>
                          <StatusBadge status={d.status === 'PROCESSED' ? 'active' : 'error'} label={d.status} />
                        </View>
                      ))
                    )}
                  </View>
                ),
              },
              {
                key: 'attempts', header: 'Attempts', width: 90,
                render: (r) => <Text style={{ color: c.ink2, fontSize: FS.md }}>{r.attempts}</Text>,
              },
              {
                key: 'error', header: 'Last error',
                render: (r) => {
                  const full = r.last_error ?? '—';
                  const isLong = full.length > ERROR_PREVIEW;
                  const open = expanded === r.id;
                  return (
                    <View>
                      <Text style={{ color: c.ink2, fontSize: FS.sm }}>
                        {open || !isLong ? full : `${full.slice(0, ERROR_PREVIEW)}…`}
                      </Text>
                      {isLong && (
                        <Text
                          onPress={() => setExpanded(open ? null : r.id)}
                          style={{ color: c.accent, fontSize: FS.sm, marginTop: 2 }}
                        >
                          {open ? 'Show less' : 'Show more'}
                        </Text>
                      )}
                    </View>
                  );
                },
              },
              {
                key: 'created', header: 'Created', width: 120,
                render: (r) => (
                  <Text style={{ color: c.ink3, fontSize: FS.md }}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </Text>
                ),
              },
              {
                key: 'actions', header: 'Actions', align: 'right', width: 190,
                render: (r) => (
                  <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
                    <PButton size="sm" kind="ghost" onPress={() => setPayloadFor(r)}>View payload</PButton>
                    <PButton
                      size="sm"
                      kind="accent"
                      onPress={() => requeue(r)}
                      disabled={requeuing === r.id}
                    >
                      {requeuing === r.id ? 'Requeuing…' : 'Requeue'}
                    </PButton>
                  </View>
                ),
              },
            ]}
          />
        )}
      </Panel>

      <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 12 }}>
        Failed events are never deleted — they leave this list by delivering successfully.
      </Text>

      {/* Read-only JSON drawer. No edit affordance: a payload that could be
          changed here would stop being evidence of what was actually sent. */}
      <SideDrawer
        open={!!payloadFor}
        onClose={() => setPayloadFor(null)}
        title="Event payload"
      >
        <Text style={{ fontFamily: 'monospace', fontSize: FS.sm, color: c.ink2 }}>
          {payloadFor ? JSON.stringify(payloadFor.payload, null, 2) : ''}
        </Text>
      </SideDrawer>
    </ScrollView>
  );
};

export default AdminDeliveryFailures;
